/* ==========================================================================
   LOVE BOMB — add to cart without leaving the page.

   The product form is a plain `<form action="/cart/add" method="post">`, so
   submitting it does a full page POST and Shopify redirects to /cart. This
   intercepts that, adds via /cart/add.js, shows a short "Added to cart"
   notification, and refreshes the cart count and drawer in place.

   Progressive: if fetch fails for any reason the form is submitted normally,
   so adding to cart never becomes impossible.
   ========================================================================== */
(function () {
  'use strict';

  var TOAST_MS = 3200;
  var toastEl = null;
  var hideTimer = null;

  function toast(message, isError) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'lb-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }

    toastEl.classList.toggle('lb-toast--error', !!isError);
    toastEl.innerHTML =
      '<span class="lb-toast__icon" aria-hidden="true">' +
      (isError
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 8v5M12 17h.01"/><circle cx="12" cy="12" r="9"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>') +
      '</span><span class="lb-toast__text"></span>';
    toastEl.querySelector('.lb-toast__text').textContent = message;

    // restart the animation even if a toast is already showing
    toastEl.classList.remove('is-visible');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');

    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, TOAST_MS);
  }

  function updateCartCount() {
    return fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var nodes = document.querySelectorAll(
          '[data-tbw-cart-count], .tbw-cart-count, .cart-count-bubble span:first-child'
        );
        nodes.forEach(function (n) {
          n.textContent = cart.item_count;
          n.classList.toggle('tbw-empty', cart.item_count === 0);
        });

        // the bespoke header carries a separate screen-reader count
        var icon = document.getElementById('cart-icon-bubble');
        var sr = icon && icon.querySelector('.visually-hidden');
        if (sr) sr.textContent = cart.item_count + ' items in cart';

        return cart;
      })
      .catch(function () {});
  }

  /* Swap a freshly rendered cart-drawer into the page, so the drawer, its
     reward ladder and its totals all reflect the new cart the next time it is
     opened. Returns true if the swap happened. */
  function swapDrawer(html) {
    var live = document.querySelector('cart-drawer');
    var host = document.getElementById('CartDrawer');
    if (!live || !host || !html) return false;

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var freshHost = doc.getElementById('CartDrawer');
    var freshEl = doc.querySelector('cart-drawer');
    if (!freshHost) return false;

    host.innerHTML = freshHost.innerHTML;

    /* The empty state is driven by `is-empty` on <cart-drawer>, which sits
       OUTSIDE the node we just swapped, so it has to be copied across from the
       response. It used to be sniffed with /is-empty/.test(html) — but the
       section ships its own `cart-drawer.is-empty { ... }` CSS, so that test
       matched every single time and the drawer was left permanently on the
       empty state. That is why the cart looked unchanged until a reload. */
    if (freshEl) {
      var open = live.classList.contains('active');
      var animating = live.classList.contains('animate');
      live.className = freshEl.className;
      if (open) live.classList.add('active');
      if (animating) live.classList.add('animate');
    }

    /* the overlay's close handler was bound to the node we just replaced */
    var overlay = host.querySelector('#CartDrawer-Overlay');
    if (overlay && typeof live.close === 'function') {
      overlay.addEventListener('click', live.close.bind(live));
    }

    return true;
  }

  /* Dawn renders the header count as its own section, so re-rendering it keeps
     the bubble's markup and its aria text in step, not just the number.

     The live header here is tbw-header, whose #cart-icon-bubble is bespoke
     markup rather than Dawn's section — dropping Dawn's HTML into it would
     replace the custom icon. So only swap when we are actually looking at
     Dawn's bubble; the bespoke header is updated by updateCartCount instead. */
  function swapCartIcon(html) {
    var host = document.getElementById('cart-icon-bubble');
    if (!host || !html) return false;
    if (host.querySelector('[data-tbw-cart-count]')) return false;
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (!fresh) return false;
    host.innerHTML = fresh.innerHTML;
    return true;
  }

  function applySections(sections) {
    if (!sections) return false;
    var drawn = swapDrawer(sections['cart-drawer']);
    swapCartIcon(sections['cart-icon-bubble']);
    return drawn;
  }

  /* Fallback for when the add response carried no sections (an older cached
     script, or a request that lost the sections params). */
  function refreshDrawer() {
    if (!document.getElementById('CartDrawer')) return Promise.resolve();

    return fetch(window.Shopify.routes.root + '?sections=cart-drawer,cart-icon-bubble')
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        applySections(data);
      })
      .catch(function () {});
  }

  /* Forms whose own component already posts to /cart/add.js and re-renders the
     drawer. Without this the item is added twice — once by that component and
     once here — and the two renders race each other. */
  var HANDLED_ELSEWHERE = 'product-form, .jhango-card__form, [data-lb-cart-skip]';

  function isAddForm(form) {
    var action = form.getAttribute('action') || '';
    if (!/\/cart\/add/.test(action)) return false;
    if (form.closest(HANDLED_ELSEWHERE)) return false;
    return true;
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement) || !isAddForm(form)) return;
      if (!window.fetch || !window.Shopify || !window.Shopify.routes) return;

      event.preventDefault();

      var button = form.querySelector('[type="submit"]:not([disabled])');
      var originalLabel = button ? button.textContent : null;
      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      }

      var body = new FormData(form);
      // ask Shopify to return the updated drawer and header count alongside
      // the add, so one round trip leaves the cart consistent
      body.append('sections', 'cart-drawer,cart-icon-bubble');
      body.append('sections_url', window.location.pathname);

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body,
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) throw data;
            return data;
          });
        })
        .then(function (item) {
          var name = (item && (item.product_title || item.title)) || 'Item';
          toast('Added to cart — ' + name);

          /* tell the rest of the theme the cart moved, so anything listening
             (quantity widgets, upsells) re-reads it */
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'lb-cart-notify' });
          }

          if (applySections(item && item.sections)) return updateCartCount();
          return Promise.all([updateCartCount(), refreshDrawer()]);
        })
        .catch(function (err) {
          var msg =
            (err && (err.description || err.message)) ||
            'Sorry, that could not be added to your cart.';
          toast(msg, true);
        })
        .then(function () {
          if (button) {
            button.removeAttribute('aria-busy');
            button.disabled = false;
            if (originalLabel !== null) button.textContent = originalLabel;
          }
        });
    },
    true
  );
})();
